<template>
  <div
    :class="[
      'notification',
      { 'notification--viewed': notificationData.isViewed },
    ]"
    @mouseenter="requestSetNotificationViewed"
  >
    <div class="notification__heading">
      <div class="notification__new" v-if="!notificationData.isViewed"></div>
      <div>
        <!-- <h3>{{ notificationData }}</h3> -->
        <NotificationPreviewTitle v-if="notificationData?.model" :notificationData="notificationData" />
        <div class="notification__date" v-if="notificationData.createdOn">
          {{ formatDate(notificationData.createdOn, SHORT_DATETIME_FORMAT) }}
        </div>
      </div>
      <div class="notification__hide">
        <el-button
          @click="requestHideNotification"
          type="primary"
          plain
          :disabled="isButtonDisabled('hide-notification')"
        >
          Hide
        </el-button>
      </div>
    </div>

    <div class="notification__info">
      <router-link
        :to="{
          name: 'EventPage',
          params: { link: notificationData.model.event.link },
        }"
        class="notification__event"
        v-if="showEventPreview"
      >
        <div class="notification__event-heading">
          <div class="notification__event-avatar">
            <el-avatar :size="32">
              <activity-image :activity="notificationData.model.event.activityId" />
            </el-avatar>
          </div>

          <div class="notification__event-name">
            {{ notificationData.model.event.name }}
          </div>

          <div class="notification__event-type">
            <el-tag type="primary" effect="dark">
              {{ notificationData.model.event.eventType }}
            </el-tag>
          </div>
        </div>

        <div class="notification__event-dates">
          <div class="notification__event-start">
            {{
              formatDate(
                notificationData.model.event.startDate,
                SHORT_DATETIME_FORMAT,
              )
            }}
          </div>

          <div
            class="notification__event-end"
            v-if="notificationData.model.event.endDate"
          >
            —
            {{
              formatDate(
                notificationData.model.event.endDate,
                SHORT_DATETIME_FORMAT,
              )
            }}
          </div>
        </div>

        <div
          class="notification__event-participant-count"
          v-if="notificationData.model.event.participantCount"
        >
          <i class="el-icon-user"></i>
          {{ notificationData.model.event.participantCount }}
          {{
            notificationData.model.event.participantCount === 1
              ? 'participant'
              : 'participants'
          }}
        </div>
      </router-link>

      <div
        class="notification__changes"
        v-if="notificationData.model.changedData"
      >
        <div
          class="notification__changes-data"
          v-if="notificationData.model.changedData.location"
        >
          <span class="notification__changes-label">
            New location:
          </span>
          <span class="notification__changes-content">
            {{ notificationData.model.changedData.location }}
          </span>
        </div>

        <div
          class="notification__changes-data"
          v-if="notificationData.model.changedData.startDate"
        >
          <span class="notification__changes-label">
            New start day:
          </span>
          <span class="notification__changes-content">
            {{
              formatDate(
                notificationData.model.changedData.startDate,
                SHORT_DATETIME_FORMAT,
              )
            }}
          </span>
        </div>

        <div
          class="notification__changes-data"
          v-if="notificationData.model.changedData.endDate"
        >
          <span class="notification__changes-label">
            New end date:
          </span>
          <span class="notification__changes-content">
            {{
              formatDate(
                notificationData.model.changedData.endDate,
                SHORT_DATETIME_FORMAT,
              )
            }}
          </span>
        </div>
      </div>

      <div class="notification__message" v-if="notificationData.model.message">
        {{ notificationData.model.message.text }}
      </div>

      <div
        class="notification__message"
        v-if="notificationData.model.application"
      >
        {{ notificationData.model.application.comments }}
      </div>
    </div>
  </div>
</template>

<script>
import EventPreview from '@/components/events/EventPreview.vue';
import NotificationPreviewTitle from '@/components/notifications/NotificationPreviewTitle.js';
import ActivityImage from '@/components/common/ActivityImage.vue';

import {
  hideNotification,
  markNotificationsAsViewed,
} from '@/api/notification.js';
import { UPDATE_USER_NOTIFICATIONS } from '@/store/mutationTypes.js';
import { SHORT_DATETIME_FORMAT, formatDate } from '@/utils/index.js';
import { showMessage } from '@/utils/messages.js';
import { disableButton } from '@/mixins/disableButton.js';

export default {
  name: '',
  props: {
    notificationData: {
      type: Object,
      required: true,
    },
  },
  mixins: [disableButton],
  components: {
    ActivityImage,
    EventPreview,
    NotificationPreviewTitle,
  },
  data() {
    return {
      SHORT_DATETIME_FORMAT,
    };
  },
  computed: {
    showEventPreview() {
      const { type } = this.notificationData;
      //console.log(type);
      switch (type) {
        case 1:
        case 4:
        case 5:
        case 6:
        case 7:
          return true;
        default:
          return false;
      }
    },
  },
  methods: {
    formatDate,
    showMessage,
    async requestHideNotification() {
      this.disableButton('hide-notification');
      const { userNotificationId } = this.notificationData;
      const { success } = await hideNotification({ userNotificationId });
      this.enableButton('hide-notification');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'The notification has been hidden',
        });
        this.$store.dispatch(UPDATE_USER_NOTIFICATIONS);
      }
    },
    async requestSetNotificationViewed() {
      const { userNotificationId } = this.notificationData;
      const userNotificationIds = [userNotificationId];

      const { success } = await markNotificationsAsViewed({
        userNotificationIds,
      });
      if (success) {
        this.$emit('update-data');
      }
    },
  },
};
</script>

<style lang="scss">
.notification {
  $this: &;

  position: relative;

  &__new {
    position: absolute;
    top: var(--global-dimension-size-150);
    left: 0;

    width: var(--global-dimension-size-125);
    height: var(--global-dimension-size-125);
    background-color: var(--color-primary-400);
    border-radius: 50%;
  }

  &__heading {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    margin-bottom: var(--global-dimension-size-200);

    margin-left: var(--global-dimension-size-250);
  }

  &__title {
    font-size: var(--font-size-300);
    display: flex;
    align-items: center;
    white-space: pre-wrap;
  }
  &__date {
    font-size: var(--font-size-100);
  }
  &__hide {
    flex-grow: 1;
    text-align: right;
  }

  &__info {
  }

  &__event {
    display: block;
    padding: var(--global-dimension-size-125);
    background-color: var(--color-primary-200);

    &:hover {
      background-color: var(--color-gray-100);

      #{$this}__event-name {
        color: var(--color-primary-400);
      }
    }
  }
  &__event-heading {
    display: flex;
    flex-flow: row nowrap;
    margin-bottom: var(--global-dimension-size-75);
  }
  &__event-avatar {
    margin-right: var(--global-dimension-size-125);
  }
  &__event-name {
    margin-right: var(--global-dimension-size-250);
  }
  &__event-type {
  }

  &__event-dates {
    display: flex;
    flex-flow: row nowrap;
    margin-bottom: var(--global-dimension-size-100);
    font-size: var(--font-size-200);
  }
  &__event-start {
  }
  &__event-end {
  }
  &__event-participant-count {
    font-size: var(--font-size-200);
  }

  &__changes {
    margin-top: var(--global-dimension-size-200);
    font-size: var(--font-size-200);
  }
  &__changes-data {
    margin-top: var(--global-dimension-size-65);
  }
  &__changes-label {
    font-weight: var(--global-font-weight-medium);
  }
  &__changes-content {
  }

  &__message {
    padding: var(--global-dimension-size-125);
    background-color: var(--color-primary-200);
    font-size: var(--font-size-200);
  }

  &--viewed {
    color: var(--color-gray-800);

    #{$this}__new {
      display: none;
    }

    #{$this}__heading {
      margin-left: 0;
    }
  }
}
</style>
